// ========================================
// Tasks Student View — Portal del Alumno
// ========================================

const TasksStudentView = {
    _tasks: [],
    _myWorks: [],
    _studentId: null,
    _tab: 'pending',
    _selectedTaskId: null,

    // Diseño C (2026-10-26): a la izquierda «Pendientes / Entregadas» con el día de entrega, a la derecha
    // la tarea elegida con su enunciado, material, formatos, mi entrega y «Entregar». Estilos: tasks.css (.tk-*).
    render() {
        return `
        <div class="dnr-proposal tk-view is-student">
            <header class="tk-head">
                <div><h1 class="tk-title">Mis tareas</h1><p class="tk-sub">Trabajos que tienes que entregar</p></div>
            </header>
            <div id="tasks-student-list"><p class="tk-empty">Cargando tareas…</p></div>
        </div>`;
    },

    async init() {
        const studentId = AuthManager.getStudentId();
        this._studentId = studentId;
        await this.loadData();
    },

    async loadData() {
        const container = document.getElementById('tasks-student-list');
        if (!container) return;
        try {
            const token = AuthManager.getCsrfToken();

            // Load tasks
            const tRes = await fetch('/api/tasks', { headers: { 'X-SICAF-CSRF': token } });
            const tData = await tRes.json();
            const allTasks = (tData.success && tData.tasks) ? tData.tasks : [];

            // Load my works (to check already submitted)
            const wRes = await fetch('/api/student-works/' + this._studentId, { headers: { 'X-SICAF-CSRF': token } });
            const wData = await wRes.json();
            this._myWorks = (wData.success && wData.works) ? wData.works : [];

            // Filter tasks visible to this student
            const student = typeof DataManager !== 'undefined' ? DataManager.getById(DataManager.KEYS.STUDENTS, this._studentId) : null;
            const myFormations = (student && student.formations) ? student.formations.map(String) : [];

            this._tasks = allTasks.filter(t => {
                const inGroup = (t.formationIds || []).some(fid => myFormations.includes(String(fid)));
                const individual = (t.studentIds || []).map(String).includes(String(this._studentId));
                return inGroup || individual;
            });

            this.renderList();
        } catch(e) {
            if (container) SafeHTML.set(container, '<p style="color:var(--danger-color);font-weight:600;">Error: ' + Utils.sanitizeHTML(e.message) + '</p>');
        }
    },

    renderList() {
        const container = document.getElementById('tasks-student-list');
        if (!container) return;

        if (this._tasks.length === 0) {
            SafeHTML.set(container, '<section class="tk-card"><p class="tk-empty">No tienes ninguna tarea asignada ahora mismo.</p></section>');
            return;
        }

        const now = new Date();
        const pending = [], delivered = [];
        this._tasks.forEach(task => {
            const mySubmissions = this._myWorks.filter(w => w.taskId === task.id);
            const latestActive = mySubmissions.find(w => !w.superseded) || mySubmissions[0];
            if (latestActive) delivered.push({ task, submission: latestActive, history: mySubmissions });
            else pending.push({ task });
        });
        const byDeadline = (a, b) => (a.task.deadline ? new Date(a.task.deadline).getTime() : Infinity) - (b.task.deadline ? new Date(b.task.deadline).getTime() : Infinity);
        pending.sort(byDeadline);
        delivered.sort((a, b) => -byDeadline(a, b));

        const list = this._tab === 'delivered' ? delivered : pending;
        if (!list.some(item => String(item.task.id) === String(this._selectedTaskId))) this._selectedTaskId = list[0]?.task.id || null;
        const chosen = list.find(item => String(item.task.id) === String(this._selectedTaskId));

        const tab = (id, label, count) => '<button type="button" class="tk-seg-btn ' + (this._tab === id ? 'is-on' : '') + '" aria-pressed="' + (this._tab === id) + '" ' +
            UIEvents.attr("click", function(event,captured){TasksStudentView.setTab(captured[0]);}, [id]) + '>' + label + ' (' + count + ')</button>';
        const items = list.map(({ task }) => {
            const due = this.dueInfo(task, now);
            const on = String(task.id) === String(this._selectedTaskId);
            return '<button type="button" class="tk-item ' + (on ? 'is-on' : '') + '" data-id="' + Utils.sanitizeHTML(task.id) + '" aria-pressed="' + on + '" ' +
                UIEvents.attr("click", function(event,captured){TasksStudentView.selectTask(String(captured[0]));}, [task.id]) + '>' +
                '<span class="tk-due ' + due.tone + '">' + Utils.sanitizeHTML(due.short) + '</span>' +
                '<span class="tk-item-main"><b>' + Utils.sanitizeHTML(task.title) + '</b><span>' + Utils.sanitizeHTML(due.long) + '</span></span></button>';
        }).join('');

        SafeHTML.set(container,
            '<div class="tk-layout">' +
                '<nav class="tk-card tk-list-panel" aria-label="Mis tareas">' +
                    '<div class="tk-list-tools"><div class="tk-seg">' + tab('pending', 'Pendientes', pending.length) + tab('delivered', 'Entregadas', delivered.length) + '</div></div>' +
                    '<div class="tk-list">' + (items || '<p class="tk-empty">' + (this._tab === 'delivered' ? 'Todavía no has entregado ninguna tarea.' : 'No tienes tareas pendientes de entrega.') + '</p>') + '</div>' +
                '</nav>' +
                '<section class="tk-card tk-detail" aria-live="polite">' +
                    (chosen ? this._renderTaskCard(chosen.task, chosen.submission || null, chosen.history || [], now) : '<p class="tk-empty">Elige una tarea de la lista.</p>') +
                '</section>' +
            '</div>');
    },

    setTab(tab) {
        this._tab = tab === 'delivered' ? 'delivered' : 'pending';
        this._selectedTaskId = null;
        this.renderList();
    },

    selectTask(taskId) {
        this._selectedTaskId = taskId;
        this.renderList();
    },

    // Same wording and tones as Gestión de tareas.
    dueInfo(task, now = new Date()) {
        const deadline = task && task.deadline ? new Date(task.deadline) : null;
        if (!deadline || Number.isNaN(deadline.getTime())) return { short: 'Sin fecha', long: 'Sin fecha límite', tone: 'is-none' };
        const days = Math.ceil((deadline - now) / 86400000);
        const long = deadline.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }) + ' a las ' + deadline.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        const short = days >= 0 && days <= 6 ? deadline.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }).replace('.', '') : deadline.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '');
        if (deadline < now) return { short, long: 'Plazo vencido el ' + long, tone: 'is-over' };
        const left = days <= 0 ? 'hoy' : days === 1 ? 'mañana' : 'en ' + days + ' días';
        return { short, long: 'Vence el ' + long + ' (' + left + ')', tone: days <= 6 ? 'is-soon' : 'is-later' };
    },

    // The chosen task: statement, material, formats, my submission and the button to hand it in.
    _renderTaskCard(task, submission, history, now) {
        const esc = value => Utils.sanitizeHTML(String(value ?? ''));
        const deadline = task.deadline ? new Date(task.deadline) : null;
        const isExpired = deadline && deadline < now;
        const hasSubmitted = !!submission;
        const isLate = hasSubmitted && deadline && new Date(submission.uploadedAt) > deadline;
        const due = this.dueInfo(task, now);
        const formatSize = b => b < 1024 * 1024 ? (b / 1024).toFixed(1) + ' KB' : (b / (1024 * 1024)).toFixed(1) + ' MB';
        const sorted = (history || []).slice().sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
        const canSend = !isExpired;
        return '<article class="tk-detail-in" data-id="' + esc(task.id) + '">' +
            '<header class="tk-detail-head"><div class="tk-detail-title">' +
                '<h2 class="tk-h2">' + esc(task.title) + '</h2>' +
                '<span class="tk-sub"><b class="tk-due-text ' + due.tone + '">' + esc(due.long) + '</b>' +
                (hasSubmitted ? ' · <span class="tk-state ' + (isLate ? 'is-late' : 'is-done') + '">' + (isLate ? 'Entregada tarde' : 'Entregada') + '</span>' : '') + '</span>' +
            '</div>' +
            (canSend ? '<button type="button" class="tk-btn is-primary" ' + UIEvents.attr('click', () => TasksStudentView.openUploadModal(task.id)) + '><i class="fas fa-upload"></i> ' + (hasSubmitted ? 'Volver a entregar' : 'Entregar') + '</button>' :
                (!hasSubmitted ? '<span class="tk-state is-over">Plazo vencido</span>' : '')) +
            '</header>' +
            '<div class="tk-detail-grid">' +
                '<div class="tk-detail-text"><span class="tk-label">Enunciado</span><p class="tk-desc">' + (task.description ? esc(task.description) : '<span class="tk-muted">Sin enunciado.</span>') + '</p>' +
                    (task.attachmentName ? '<a class="tk-attach" href="/api/tasks/attachment/' + encodeURIComponent(task.attachmentId) + '" target="_blank" rel="noopener"><i class="fas fa-paperclip"></i> ' + esc(task.attachmentName) + '</a>' : '') + '</div>' +
                '<div class="tk-detail-facts"><div><span class="tk-label">Formatos y tamaño</span><span class="tk-fact">' + esc((task.allowedTypes || []).length ? task.allowedTypes.join(', ') : 'Cualquiera') + ' · hasta ' + esc(task.maxSizeMB || 100) + ' MB</span></div></div>' +
            '</div>' +
            '<div class="tk-subs-wrap"><div class="tk-subs-head"><span class="tk-label">Mi entrega</span></div>' +
                (hasSubmitted ? '<div class="tk-subs">' + sorted.map((h, i) => '<div class="tk-sub"><b class="tk-sub-name">' + (i === 0 ? 'Actual' : 'Anterior') + '</b><span class="tk-sub-file' + (h.superseded ? ' is-old' : '') + '">' + esc(h.originalName) + ' · ' + formatSize(h.size || 0) + ' · ' + new Date(h.uploadedAt).toLocaleDateString('es-ES') + '</span></div>').join('') + '</div>'
                    : '<p class="tk-empty">' + (isExpired ? 'No la entregaste a tiempo.' : 'Todavía no la has entregado.') + '</p>') +
            '</div>' +
        '</article>';
    },

    openUploadModal(taskId) {
        const task = this._tasks.find(t => t.id === taskId);
        if (!task) return;
        const acceptTypes = (task.allowedTypes && task.allowedTypes.length) ? task.allowedTypes.map(t => '.' + t).join(',') : '*';
        const maxMB = task.maxSizeMB || 100;

        SafeHTML.fire({
            title: '<span style="font-size:1rem;font-weight:800;display:inline-flex;align-items:center;gap:8px;"><i class="fas fa-upload" style="color:#2563eb;"></i>Entregar tarea</span>',
            html: '<div style="text-align:left;">' +
                '<p style="font-size:0.85rem;color:#475569;margin:0 0 1rem 0;font-weight:600;">' + Utils.sanitizeHTML(task.title) + '</p>' +
                '<div style="background:#eff6ff;border-radius:10px;padding:0.75rem 1rem;margin-bottom:1rem;font-size:0.8rem;color:#1e40af;">' +
                    '<strong>Formatos aceptados:</strong> ' + (task.allowedTypes && task.allowedTypes.length ? Utils.sanitizeHTML(task.allowedTypes.join(', ')) : 'Cualquiera') + ' · <strong>Máx.</strong> ' + maxMB + ' MB' +
                '</div>' +
                `<div style="border:2px dashed #cbd5e1;border-radius:10px;padding:1.5rem;text-align:center;cursor:pointer;margin-bottom:0.75rem;" ${UIEvents.attr("click", function(event,captured){document.getElementById('task-file-input').click();}, [])}>` +
                    '<i class="fas fa-cloud-upload-alt" style="font-size:2rem;color:#94a3b8;display:block;margin-bottom:0.5rem;"></i>' +
                    '<p style="margin:0;font-size:0.85rem;color:#64748b;">Haz clic para seleccionar tu archivo</p>' +
                    '<input type="file" id="task-file-input" accept="' + Utils.sanitizeHTML(acceptTypes) + `" style="display:none;" ${UIEvents.attr("change", function(event,captured){document.getElementById('task-file-name').textContent=this.files[0]?this.files[0].name:'';}, [])}>` +
                    '<p id="task-file-name" style="margin:0.5rem 0 0 0;font-size:0.82rem;font-weight:700;color:#2563eb;"></p>' +
                '</div>' +
                '<div><label style="font-size:0.82rem;font-weight:700;display:block;margin-bottom:4px;">Notas o comentarios (opcional)</label><textarea id="task-notes" rows="2" style="width:100%;border:1px solid #d1d5db;border-radius:8px;padding:8px 12px;font-size:0.85rem;box-sizing:border-box;font-family:inherit;resize:vertical;" placeholder="Añade algún comentario para el profesor..."></textarea></div>' +
            '</div>',
            showCancelButton: true,
            confirmButtonText: 'Enviar entrega',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2563eb',
            preConfirm: () => {
                const fileInput = document.getElementById('task-file-input');
                if (!fileInput || !fileInput.files || !fileInput.files[0]) {
                    Swal.showValidationMessage('Selecciona un archivo antes de enviar');
                    return false;
                }
                const file = fileInput.files[0];
                if (file.size > maxMB * 1024 * 1024) {
                    Swal.showValidationMessage('El archivo supera el tamaño máximo de ' + maxMB + ' MB');
                    return false;
                }
                return { file, notes: document.getElementById('task-notes').value };
            }
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                SafeHTML.fire({ title: 'Subiendo...', html: '<i class="fas fa-spinner fa-spin" style="font-size:2rem;color:#2563eb;"></i>', showConfirmButton: false, allowOutsideClick: false });
                const formData = new FormData();
                formData.append('file', result.value.file);
                formData.append('title', task.title);
                formData.append('notes', result.value.notes || '');
                formData.append('taskId', taskId);
                const token = AuthManager.getCsrfToken();
                const res = await fetch('/api/student-works/' + this._studentId + '?taskId=' + encodeURIComponent(taskId), { method: 'POST', headers: { 'X-SICAF-CSRF': token }, body: formData });
                const data = await res.json();
                if (!data.success) throw new Error(data.error);
                SafeHTML.fire({ icon: 'success', title: '¡Entrega enviada!', text: 'Tu archivo ha sido enviado correctamente.', timer: 2000, showConfirmButton: false });
                setTimeout(() => TasksStudentView.loadData(), 2100);
            } catch(e) {
                SafeHTML.fire('Error', e.message, 'error');
            }
        });
    }
};

window.TasksStudentView = TasksStudentView;
